import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import { format } from 'date-fns';
import SegmentedProgressBar from '../shared/SegmentedProgressBar';

const OperationDetail = ({ reservation, dockName, onBack, onStatusUpdate, canExecute }) => {
    // Timer logic for progress
    const [elapsedMinutes, setElapsedMinutes] = useState(0);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        if (!reservation) return;

        const updateTimer = () => {
            // Also consider 'arrived' or other active statuses if needed, but primary is 'loading'
            if (['loading', 'loading_start', 'arrived'].includes(reservation.status)) {
                // Prioritize actualStartTime, then fallback to startTime for estimation if needed, but requested to log actual.
                // If actualStartTime is missing, maybe it hasn't been set yet properly?
                const startTimeStr = reservation.actualStartTime;

                if (startTimeStr) {
                    const start = new Date(startTimeStr);
                    if (!isNaN(start.getTime())) {
                        const now = new Date();
                        const diff = Math.floor((now - start) / 60000); // minutes
                        setElapsedMinutes(Math.max(0, diff));
                    } else {
                        setElapsedMinutes(0);
                    }
                } else {
                    setElapsedMinutes(0);
                }
            } else {
                setElapsedMinutes(0);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 5000); // Update every 5 seconds for smoother UI
        return () => clearInterval(interval);
    }, [reservation]);


    if (!reservation) return null;

    // Calculate duration from slot times (real occupied time)
    // Round up to nearest 20 mins
    const diffMins = (new Date(reservation.endTime) - new Date(reservation.startTime)) / 60000;
    const plannedDuration = diffMins > 0 ? Math.round(diffMins) : (reservation.plannedDuration || 60);

    // Protect against division by zero or NaN
    const validElapsed = isNaN(elapsedMinutes) ? 0 : elapsedMinutes;
    const progressPct = plannedDuration > 0 ? Math.min((validElapsed / plannedDuration) * 100, 100) : 0;
    const remaining = Math.max(plannedDuration - validElapsed, 0);

    // Button Logic
    let btnText = 'GİRİŞ';
    let btnClass = 'btn-start';
    let nextStatus = 'loading';

    if (reservation.status === 'loading') {
        btnText = 'ÇIKIŞ';
        btnClass = 'btn-stop';
        nextStatus = 'completed';
    } else if (reservation.status === 'completed') {
        btnText = 'Devam';
        btnClass = 'btn-start';
        nextStatus = 'loading';
    }

    const handleAction = () => {
        if (!nextStatus) return;

        if (nextStatus === 'completed') {
            setShowConfirmModal(true);
        } else {
            onStatusUpdate(reservation.id, nextStatus);
        }
    };

    const confirmCompletion = () => {
        setShowConfirmModal(false);
        onStatusUpdate(reservation.id, 'completed');
    };

    const shortName = reservation.transporterName ? reservation.transporterName.split(' ')[0] : 'Firma';

    // Determine Glow Color based on button state
    let glowColor = 'transparent';
    if (btnClass === 'btn-start') glowColor = 'rgba(34, 197, 94, 0.4)';
    if (btnClass === 'btn-stop') glowColor = 'rgba(239, 68, 68, 0.4)';

    return (
        <div className="op-detail-container">
            {/* Dock Name Header - Centered Above Card - High Negative Margin */}
            <div className="w-full text-center mb-6 -mt-36">
                <h1 className="text-3xl font-black text-white tracking-wider uppercase drop-shadow-lg" style={{
                    background: 'linear-gradient(180deg, #fff 0%, #aaa 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.5))'
                }}>
                    {dockName || 'Rampa'}
                </h1>
            </div>

            {/* Info Card - Left Aligned */}
            <div
                className="op-info-card text-left"
                style={{
                    boxShadow: glowColor !== 'transparent' ? `0 0 30px ${glowColor}` : 'none',
                    border: glowColor !== 'transparent' ? `1px solid ${glowColor.replace('0.4', '0.2')}` : '1px solid var(--border-color)',
                    transition: 'all 0.3s ease'
                }}
            >
                <div className="op-card-header flex flex-col">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Rampadaki Araç</span>
                    <h2 className="op-company-name">{shortName}</h2>
                </div>

                <div className="op-card-body">
                    {/* Driver Name Row */}
                    <div className="op-info-row">
                        <span className="op-label">Sürücü:</span>
                        <span className="op-value">{reservation.driverName || '---'}</span>
                    </div>

                    <div className="op-info-row">
                        <span className="op-label">Plaka:</span>
                        <span className="op-value font-mono">{reservation.plateNumber || reservation.plate || '---'}</span>
                    </div>

                    <div className="op-info-row">
                        <span className="op-label">Rezervasyon:</span>
                        <span className="op-value font-mono">
                            {format(new Date(reservation.startTime), 'HH:mm')} - {format(new Date(reservation.endTime), 'HH:mm')}
                        </span>
                    </div>

                    {/* Gate Entry Time - New Field */}
                    <div className="op-info-row">
                        <span className="op-label">Rampa Giriş:</span>
                        <span className="op-value font-mono">
                            {(() => {
                                if (!reservation.actualStartTime) return '--:--';
                                const d = new Date(reservation.actualStartTime);
                                return isNaN(d.getTime()) ? '--:--' : format(d, 'HH:mm');
                            })()}
                        </span>
                    </div>

                    <div className="op-info-row">
                        <span className="op-label">Hacim / Ağırlık:</span>
                        <span className="op-value">
                            {reservation.volume || reservation.metrics?.volume || 0} m³ / {reservation.weight || reservation.metrics?.weight || 0} kg
                        </span>
                    </div>
                </div>
            </div>

            {/* Progress Section - Always Visible */}
            <div className="op-progress-section mb-6">
                <div className="op-time-labels mb-2 flex justify-between text-sm font-medium text-gray-300">
                    <span>{plannedDuration} DK Planlanan</span>
                    <span>{validElapsed} DK Geçen</span>
                </div>

                {/* Using SegmentedProgressBar for consistency */}
                <div className="w-full mt-4">
                    <SegmentedProgressBar
                        value={progressPct}
                        label="İŞLEM SÜRECİ"
                        reverse={true}
                        color={remaining > 15 ? '#22c55e' : remaining > 0 ? '#f59e0b' : '#ef4444'}
                    />
                </div>

                <div className="text-right text-xl font-bold mt-2" style={{ color: remaining === 0 ? '#ef4444' : '#22c55e' }}>
                    Kalan: {remaining} DK
                </div>
            </div>

            {/* Action Button */}
            <div className="py-4 flex justify-center">
                <button
                    className={`big-action-btn ${btnClass}`}
                    onClick={handleAction}
                    disabled={!nextStatus || !canExecute}
                    style={{ filter: `drop-shadow(0 0 10px ${glowColor})`, opacity: !canExecute ? 0.5 : 1 }}
                >
                    {btnText}
                </button>
            </div>

            {/* Back Button - Moved to Bottom Left with Inline Glass Theme */}
            <div className="w-full flex justify-start mt-auto pt-24 pb-2">
                <button
                    onClick={onBack}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        color: 'white',
                        boxShadow: '0 0 15px rgba(255, 255, 255, 0.2)', // White Glow
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 255, 255, 0.4)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 255, 255, 0.2)';
                    }}
                >
                    <span style={{ fontSize: '1rem' }}>←</span> GERİ
                </button>
            </div>

            {/* Confirmation Modal - With RED glow matches the STOP button */}
            <Modal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                title="İşlem Onayı"
                size="sm"
                shadowColor="rgba(239, 68, 68, 0.6)" // Explicit Red Glow for Stop Confirmation
                footer={
                    <div className="flex justify-end gap-3 w-full">
                        <Button
                            variant="secondary"
                            onClick={() => setShowConfirmModal(false)}
                        >
                            İptal
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmCompletion}
                        >
                            Tamamla
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col items-center justify-center p-4 text-center">

                    <p className="text-lg text-white font-medium">
                        Yükleme işlemini tamamlamak istiyor musunuz?
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default OperationDetail;
